import React, { FC, ReactNode, useEffect, useState } from 'react';
import { EditPostContext } from './context';
import { EditPostType, PostType } from 'models/posts.model';
import { convertTimeString, handleApiError } from 'utils/functions';
import { apiService } from 'services/base.service';
import { useParams } from 'react-router-dom';
import { PolygonType } from 'models/polygon.model';
import { BrandType } from 'models/brand.model';
import { MealType } from 'models/meal.model';

const EditPostProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [postToEdit, setPostToEdit] = useState<PostType | null>(null);
  const [editPostForm, setEditPostForm] = useState({} as EditPostType);
  const [initialEditData, setInitialEditData] = useState({} as EditPostType);
  const [disabled, setDisabled] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [polygons, setPolygons] = useState<PolygonType[]>([]);
  const { postId } = useParams();
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [meals, setMeals] = useState<MealType[]>([]);

  const fetchPostToEdit = async () => {
    try {
      setPostLoading(true);
      const res = await apiService.get(`marketing/post/${postId}`);
      setPostToEdit(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setPostLoading(false);
    }
  };

  const loadPolygons = async (id: string) => {
    try {
      const res = await apiService.get(`internal/cokitchen/polygons/${id}`, {
        isInternal: true
      });
      setPolygons(res.data.data);
    } catch (error) {
      setPolygons([]);
      handleApiError(error);
    }
  };

  const fetchBrands = async () => {
    if (!editPostForm.cokitchen_id) return;
    try {
      const res = await apiService.post('brands/fetch', {
        data: {
          cokitchen_ids: [editPostForm.cokitchen_id]
        }
      });
      setBrands(res.data.data);
    } catch (error) {
      setBrands([]);
      handleApiError(error);
    }
  };

  const fetchMeals = async () => {
    if (!editPostForm.brand_id) return;
    try {
      const res = await apiService.get('meal/bulk/brands', {
        params: {
          brand_ids: [editPostForm.brand_id]
        }
      });
      setMeals(res.data.data);
    } catch (error) {
      setMeals([]);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchPostToEdit();
  }, [postId]);

  useEffect(() => {
    if (postToEdit) {
      const formattedPostData = {
        ...editPostForm,
        heading: postToEdit.heading,
        title: postToEdit.title,
        body: postToEdit.body,
        image: postToEdit.images
          ? Array.isArray(postToEdit.images)
            ? postToEdit.images
            : JSON.parse(postToEdit.images || '')
          : [],
        location: postToEdit.polygon_ids
          ? Array.isArray(postToEdit.polygon_ids)
            ? postToEdit.polygon_ids
            : JSON.parse(postToEdit.polygon_ids || '')
          : [],
        start_date: new Date(postToEdit.to_start_date),
        end_date: postToEdit.to_expire_date ? new Date(postToEdit.to_expire_date) : undefined,
        start_time: convertTimeString(postToEdit.to_start_time),
        end_time: postToEdit.to_expire_time ? convertTimeString(postToEdit.to_expire_time) : null,
        cokitchen_id: postToEdit.cokitchen_id,
        meal_id: postToEdit.meal_id || '',
        brand_id: postToEdit.brand_id || '',
        post_type: postToEdit.post_type || 'fcb'
      };
      setEditPostForm(formattedPostData);
      setInitialEditData(formattedPostData);
    }
  }, [postToEdit]);

  useEffect(() => {
    if (editPostForm.cokitchen_id) {
      loadPolygons(editPostForm.cokitchen_id);
      fetchBrands();
    }
  }, [editPostForm.cokitchen_id]);

  useEffect(() => {
    fetchMeals();
  }, [editPostForm.brand_id]);

  return (
    <EditPostContext.Provider
      value={{
        editPostForm,
        setEditPostForm,
        disabled,
        setDisabled,
        postToEdit,
        setPostToEdit,
        postLoading,
        initialEditData,
        polygons,
        meals,
        brands
      }}>
      {children}
    </EditPostContext.Provider>
  );
};

export default EditPostProvider;
